import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Volume2, VolumeX } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { audioNotifier } from "@/lib/audio-notifier";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface AudioSettings {
  audioNotificationsEnabled: boolean;
}

export function SpeakerToggle() {
  const { toast } = useToast();

  const { data: settings, isLoading } = useQuery<AudioSettings>({
    queryKey: ['/api/user/audio-settings'],
  });

  const toggleMutation = useMutation({
    mutationFn: async () => {
      const newValue = !settings?.audioNotificationsEnabled;
      await apiRequest('PATCH', '/api/user/audio-settings', {
        audioNotificationsEnabled: newValue,
      });
      return newValue;
    },
    onSuccess: (enabled) => {
      queryClient.invalidateQueries({ queryKey: ['/api/user/audio-settings'] });
      
      if (enabled) {
        audioNotifier.unlockAudioContext();
      }
      
      toast({
        title: enabled ? "Sounds enabled" : "Sounds muted",
        description: enabled 
          ? "You will hear notification sounds"
          : "Notification sounds are muted",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to toggle sounds",
        variant: "destructive",
      });
    },
  });

  const isEnabled = settings?.audioNotificationsEnabled ?? false;

  if (isLoading) {
    return null;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => toggleMutation.mutate()}
            disabled={toggleMutation.isPending}
            className={`h-8 w-8 p-0 ${isEnabled ? 'text-blue-400 hover:text-blue-300' : 'text-orange-400 hover:text-orange-300'}`}
            data-testid="button-speaker-toggle"
          >
            {isEnabled ? (
              <Volume2 className="w-4 h-4" />
            ) : (
              <VolumeX className="w-4 h-4" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{isEnabled ? 'Sounds on (click to mute)' : 'Sounds muted (click to enable)'}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
